import React, { useMemo } from 'react'
import TripCarouselCards from './TripCarouselCards'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import { useExploreItems } from 'tripPlanner/hooks/api/explore'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import TripCarouselCardsLoading from './TripCarouselCardsLoading'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import { ExploreCategory } from 'tripPlanner/pages/trip/Explore/common'

interface Props {
  type: ExploreCategory['label']
  className?: string
  lat: number | undefined
  lng: number | undefined
  location: string | undefined
  tripId: string | undefined
  showDistance: boolean
}

const TransparentCard = styled.div`
  height: ${rem(115)};
  width: ${rem(312)};
  background: rgba(255,255,255, 0.16);
`

function TripCarousel({ type, className, lat, lng, location, tripId, showDistance }: Props) {
  const { data: items, isLoading, isFetching, isRefetching, isError } = useExploreItems({
    query: `${type} in ${location}`,
    locationBias: lat !== undefined && lng !== undefined ? [lat, lng] : undefined,
  })

  const itemsWithDistance = useMemo(() => {
    if (!items) return []
    return items.map((item) => ({
      ...item,
      distance: showDistance && lat !== undefined && lng !== undefined
        ? calculateDistance(lat, lng, item.place.point.lat, item.place.point.lng)
        : undefined,
    }))
  }, [items, lat, lng, showDistance])

  const showLoading = isLoading || isFetching || isRefetching || (!items && !isError)
  const showContent = !showLoading && itemsWithDistance.length > 0

  return (
    <FullWidthCarousel
      className={className}
      snap="start"
      gap={16}
    >
      {showLoading && (
        <>
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
        </>
      )}
      {showContent && itemsWithDistance.slice(0, 5).map((item) => (
        <TripCarouselCards
          key={item.id}
          item={item}
          distance={item.distance}
          showDistance={showDistance}
          tripId={tripId}
        />
      ))}
      <TransparentCard>
        <Sheet padding={16}>
          <Group direction="vertical" gap={12} verticalAlign="space-between">
            <Group direction="vertical" gap={4} horizontalAlign="center" verticalAlign="center">
              <BodyText variant="small" weight="bold">
                Add more to your trip
              </BodyText>
              <BodyText variant="small">
                Your escape, your way - dining, sights & more.
              </BodyText>
            </Group>
            <Group direction="horizontal" horizontalAlign="center" verticalAlign="center">
              {tripId && <TextButton kind="secondary" size="small" to={`/trip-planner/trip/${tripId}`}>
                View trip plan
              </TextButton>}
              {!tripId && <TextButton kind="secondary" size="small" to={`/trip-planner/generate-trip?location=${location}`}>
                Start planning - It's free
              </TextButton>}
            </Group>
          </Group>
        </Sheet>
      </TransparentCard>
    </FullWidthCarousel>
  )
}

export default TripCarousel
